import { action, makeObservable, observable } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import {
  documentsPermissionsResponseSchema,
  DocumentPermission,
  DocumentsPermissionsResponse,
  DocumentsPermissionsResponseApi,
  GlobalPermissions,
} from '@/entities/Permissions';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import api from '@/utils/api';
import { schemeParse } from '@/utils/schemeParse';

type DocumentsPermissionsStoreParams = {
  getOrganizationId: () => number;
};

export type PermissionsRequestBody = {
  id: string;
  type: 'document' | 'folder';
};

type PrivateFields = '_permissions' | '_setPermissions' | '_globalPermissions';

export class DocumentsPermissionsStore implements ILocalStore {
  private readonly _getOrganizationId: () => number;
  private readonly _permissions = new Map<string, DocumentPermission>();
  private _globalPermissions: GlobalPermissions = {
    canCreate: false,
    canUploadToPersonal: false,
    canCreateInPersonal: false,
  };

  readonly meta = new MetaStore();

  constructor({ getOrganizationId }: DocumentsPermissionsStoreParams) {
    this._getOrganizationId = getOrganizationId;

    makeObservable<DocumentsPermissionsStore, PrivateFields>(this, {
      _permissions: observable.shallow,
      _globalPermissions: observable.ref,
      _setPermissions: action.bound,
      reset: action.bound,
    });
  }

  private _composeKey(id: string, type: PermissionsRequestBody['type']): string {
    const formattedType = type === 'folder' ? 'folder' : 'document';

    return `${formattedType}:${id}`;
  }

  private _filterRequestItems(items: PermissionsRequestBody[]): PermissionsRequestBody[] {
    const seen = new Set<string>();

    return items.filter(({ id, type }) => {
      const key = this._composeKey(id, type);

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);

      return !this._permissions.has(key);
    });
  }

  private _setPermissions(
    permissions: DocumentsPermissionsResponse['permissions'],
    requestedItems: PermissionsRequestBody[],
    globalPermissions: DocumentsPermissionsResponse['globalPermissions'],
  ): void {
    Object.entries(permissions).forEach(([objectId, permission]) => {
      this._permissions.set(objectId, permission);
    });

    requestedItems.forEach(({ id, type }) => {
      const key = this._composeKey(id, type);

      if (!permissions[key]) {
        this._permissions.set(key, { canDelete: true, canEdit: true, canUpload: false });
      }
    });

    this._globalPermissions = globalPermissions;
  }

  async fetchPermissions(items: PermissionsRequestBody[]): Promise<boolean> {
    const organizationId = this._getOrganizationId();

    if (!organizationId) {
      return false;
    }

    const requestItems = this._filterRequestItems(items);

    if (requestItems.length === 0) {
      return true;
    }

    this.meta.setLoadedStartMeta();
    const response = await api<DocumentsPermissionsResponseApi>({
      endpoint: ENDPOINTS.permissions.get(String(organizationId)),
      method: 'POST',
      data: { items: requestItems },
    });

    if (response.isError) {
      this.meta.setLoadedErrorMeta();

      return false;
    }

    const parsed = schemeParse(documentsPermissionsResponseSchema, response.data);

    if (!parsed.success) {
      this.meta.setLoadedErrorMeta();

      return false;
    }

    this._setPermissions(parsed.data.permissions, requestItems, parsed.data.globalPermissions);
    this.meta.setLoadedSuccessMeta();

    return true;
  }

  canDelete(id: string, type: PermissionsRequestBody['type']): boolean {
    const key = this._composeKey(id, type);

    return this._permissions.get(key)?.canDelete ?? true;
  }

  hasPermission(id: string, type: PermissionsRequestBody['type']): boolean {
    const key = this._composeKey(id, type);

    return this._permissions.has(key);
  }

  canEdit(id: string, type: PermissionsRequestBody['type']): boolean {
    const key = this._composeKey(id, type);

    return this._permissions.get(key)?.canEdit ?? true;
  }

  canCreate(): boolean {
    return this._globalPermissions.canCreate;
  }

  canUploadToPersonal(): boolean {
    return this._globalPermissions.canUploadToPersonal;
  }

  canCreateInPersonal(): boolean {
    return this._globalPermissions.canCreateInPersonal;
  }

  canUpload(id: string, type: PermissionsRequestBody['type']): boolean {
    const key = this._composeKey(id, type);

    return this._permissions.get(key)?.canUpload ?? false;
  }

  get globalPermissions(): GlobalPermissions {
    return this._globalPermissions;
  }

  reset(): void {
    this.meta.reset();
    this._permissions.clear();
    this._globalPermissions = { canCreate: true, canUploadToPersonal: false, canCreateInPersonal: false };
  }

  destroy(): void {
    this.meta.destroy();
  }
}
